// /cash-closing — Cash Closing History (PAGES.md §3.100)
//
// View history of all cash closings across locations. Drill into
// denomination breakdown. MANAGER+ (30). FR-BILL-048

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Badge,
  Select,
  DatePicker,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Pagination,
  Modal,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { cashClosingAdminService } from '@/services/cash-closing.service';
import type { CashClosingRecord } from '@/services/cash-closing.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, cn } from '@/constants/design-tokens';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { getDenominationFaces } from '@bbpos/types';
import type { CurrencyCode } from '@bbpos/types';
import { getSubunitMultiplier } from '@bbpos/utils';
import { toLocalIsoDate } from './date-utils';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
const VARIANCE_THRESHOLD_PCT = 2;

// ─── Icons ─────────────────────────────────────────────────────────────────

function CalculatorIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="10" x2="8" y2="10.01" /><line x1="12" y1="10" x2="12" y2="10.01" /><line x1="16" y1="10" x2="16" y2="10.01" /><line x1="8" y1="14" x2="8" y2="14.01" /><line x1="12" y1="14" x2="12" y2="14.01" /><line x1="16" y1="14" x2="16" y2="14.01" /><line x1="8" y1="18" x2="16" y2="18" />
    </svg>
  );
}

function CalculatorEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="10" x2="8" y2="10.01" /><line x1="12" y1="10" x2="12" y2="10.01" /><line x1="16" y1="10" x2="16" y2="10.01" /><line x1="8" y1="14" x2="8" y2="14.01" /><line x1="12" y1="14" x2="12" y2="14.01" /><line x1="16" y1="14" x2="16" y2="14.01" /><line x1="8" y1="18" x2="16" y2="18" />
    </svg>
  );
}

function CurrencyIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="6" y1="3" x2="6" y2="15" /><path d="M6 3h8a4 4 0 010 8H6" /><path d="M14 11l4 10" /><line x1="4" y1="3" x2="12" y2="3" /><line x1="4" y1="7" x2="16" y2="7" />
    </svg>
  );
}

function AlertIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

function varianceColor(variance: number): string {
  if (variance === 0) return 'text-success-600 dark:text-success-400';
  if (variance > 0) return 'text-warning-600 dark:text-warning-400';
  return 'text-danger-600 dark:text-danger-400';
}

type VarianceBadge = 'success' | 'info' | 'danger';

function varianceBadge(variance: number): { variant: VarianceBadge; label: string } {
  if (variance === 0) return { variant: 'success', label: 'Match' };
  if (variance > 0) return { variant: 'info', label: 'Over' };
  return { variant: 'danger', label: 'Short' };
}

// ─── Mobile Card ────────────────────────────────────────────────────────────

function ClosingMobileCard({ record, onClick }: { record: CashClosingRecord; onClick: () => void }) {
  const currency = useTenantCurrency();
  const badge = varianceBadge(record.variance);
  return (
    <div className={cn(GLASS, 'p-4 animate-slide-up cursor-pointer transition-all duration-200 hover:shadow-lg')} onClick={onClick}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{record.closed_by_name ?? 'Unknown'}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{record.location_name}</p>
        </div>
        <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className={cn('text-sm font-bold font-mono tabular-nums', varianceColor(record.variance))}>
          {record.variance >= 0 ? '+' : ''}{formatPaise(record.variance, currency)}
        </span>
        <span className="text-xs font-mono tabular-nums text-neutral-400 dark:text-neutral-500">{formatDate(record.closed_at)}</span>
      </div>
    </div>
  );
}

// ─── Denomination Modal ─────────────────────────────────────────────────────

function DenominationBreakdown({ record, currency }: { record: CashClosingRecord; currency: CurrencyCode }) {
  // Faces come from the tenant's own ladder, not a hardcoded Indian one, and
  // are already exact minor units, so the old hardcoded hundred-multiplier —
  // which under-read every 3-decimal currency by 10x — is gone.
  const subunit = getSubunitMultiplier(currency);
  const faces = getDenominationFaces(currency).map((f) => ({
    ...f,
    /** Major-unit face — also the `denominations` JSONB key. */
    major: f.minor / subunit,
  }));
  const countOf = (major: number) => record.denominations[String(major)] ?? 0;
  const total = faces.reduce((sum, f) => sum + f.minor * countOf(f.major), 0);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
        <span>Denomination</span><span className="text-center">Count</span><span className="text-right">Amount</span>
      </div>
      {faces.map((f) => {
        const count = countOf(f.major);
        return (
          <div key={f.major} className="grid grid-cols-3 gap-2 items-center py-1">
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{getCurrencySymbol(currency)}{f.major.toLocaleString('en-IN')}</span>
            <span className="text-base font-semibold font-mono tabular-nums text-neutral-900 dark:text-neutral-100 text-center">{count}</span>
            <span className="text-sm font-semibold font-mono tabular-nums text-neutral-700 dark:text-neutral-300 text-right">{formatPaise(f.minor * count, currency)}</span>
          </div>
        );
      })}
      <div className="flex items-center justify-between pt-3 mt-2">
        <span className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Total</span>
        <span className="text-lg font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{formatPaise(total, currency)}</span>
      </div>
      <div className="flex items-center justify-between pt-2">
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Expected</span>
        <span className="text-sm font-bold font-mono tabular-nums text-neutral-700 dark:text-neutral-300">{formatPaise(record.expected_cash, currency)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Variance</span>
        <span className={cn('text-lg font-bold font-mono tabular-nums', varianceColor(record.variance))}>
          {record.variance >= 0 ? '+' : ''}{formatPaise(record.variance, currency)} ({record.variance_pct >= 0 ? '+' : ''}{record.variance_pct.toFixed(1)}%)
        </span>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function CashClosingHistoryContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = useAuthStore((s) => s.profile);
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  const urlPage = Number(searchParams.get('page') ?? '1');
  const urlLocation = searchParams.get('location_id') ?? '';
  const urlStartDate = searchParams.get('start_date') ?? '';
  const urlEndDate = searchParams.get('end_date') ?? '';

  const [page, setPage] = useState(urlPage);
  const [locationFilter, setLocationFilter] = useState(urlLocation || selectedLocationId || '');
  const [startDate, setStartDate] = useState(urlStartDate);
  const [endDate, setEndDate] = useState(urlEndDate);

  const [records, setRecords] = useState<CashClosingRecord[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState<{ avg_variance_pct: number; shortfall_count: number }>({ avg_variance_pct: 0, shortfall_count: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [detailRecord, setDetailRecord] = useState<CashClosingRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const locationOptions = useMemo(() => [
    { value: '', label: 'All Locations' },
    ...((profile?.locations ?? [])
      .filter((location) => location.is_active)
      .map((location) => ({ value: location.id, label: location.name }))),
  ], [profile?.locations]);

  useEffect(() => { document.title = 'Cash Closing — Orio360 Admin'; }, []);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (locationFilter) params.location_id = locationFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [locationFilter, startDate, endDate, page, setSearchParams]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [result, statsResult] = await Promise.all([
        cashClosingAdminService.list({
          page,
          location_id: locationFilter || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        }),
        cashClosingAdminService.stats({
          location_id: locationFilter || undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        }),
      ]);
      setRecords(result.data);
      setTotalItems(result.meta.total);
      setStats({ avg_variance_pct: statsResult.avg_variance_pct, shortfall_count: statsResult.shortfall_count });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load closings', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [page, locationFilter, startDate, endDate, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = useCallback((record: CashClosingRecord) => {
    setDetailRecord(record);
    setModalOpen(true);
  }, []);

  // Dataset-scoped stats (computed server-side over the whole filtered range,
  // independent of pagination — so the cards stay stable across pages, #176).
  const avgVariancePct = stats.avg_variance_pct;
  const shortfallCount = stats.shortfall_count;

  const columns: ColumnDef<CashClosingRecord>[] = useMemo(() => [
    {
      key: 'closed_at', header: 'Date',
      render: (_v: unknown, row: CashClosingRecord) => (
        <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">{formatDate(row.closed_at)}</span>
      ),
    },
    {
      key: 'location_name', header: 'Location',
      render: (_v: unknown, row: CashClosingRecord) => (
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{row.location_name}</span>
      ),
    },
    {
      key: 'closed_by_name', header: 'Cashier',
      render: (_v: unknown, row: CashClosingRecord) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400">{row.closed_by_name ?? 'Unknown'}</span>
      ),
    },
    {
      key: 'shift_name', header: 'Shift',
      render: (_v: unknown, row: CashClosingRecord) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400">{row.shift_name ?? '—'}</span>
      ),
    },
    {
      key: 'expected_cash', header: 'Expected', numeric: true,
      render: (_v: unknown, row: CashClosingRecord) => (
        <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{formatPaise(row.expected_cash, currency)}</span>
      ),
    },
    {
      key: 'actual_cash', header: 'Actual', numeric: true,
      render: (_v: unknown, row: CashClosingRecord) => (
        <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">{formatPaise(row.actual_cash, currency)}</span>
      ),
    },
    {
      key: 'variance', header: 'Variance', numeric: true,
      render: (_v: unknown, row: CashClosingRecord) => (
        <span className={cn('text-sm font-bold font-mono tabular-nums', varianceColor(row.variance))}>
          {row.variance >= 0 ? '+' : ''}{formatPaise(row.variance, currency)}
        </span>
      ),
    },
    {
      key: 'variance_pct', header: 'Var %', numeric: true,
      render: (_v: unknown, row: CashClosingRecord) => (
        <span className={cn(
          'text-xs font-semibold font-mono tabular-nums',
          varianceColor(row.variance),
          Math.abs(row.variance_pct) > VARIANCE_THRESHOLD_PCT && 'bg-danger-50 dark:bg-danger-900/20 px-1.5 py-0.5 rounded',
        )}>
          {row.variance_pct >= 0 ? '+' : ''}{row.variance_pct.toFixed(1)}%
        </span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: CashClosingRecord) => {
        const b = varianceBadge(row.variance);
        return <Badge variant={b.variant} size="sm">{b.label}</Badge>;
      },
    },
    {
      // DataTable has no row-click prop, so the desktop drill-down is an explicit
      // button — without it the breakdown was only reachable from the mobile cards.
      key: 'id', header: '',
      render: (_v: unknown, row: CashClosingRecord) => (
        <button
          type="button"
          onClick={() => openDetail(row)}
          className="text-xs font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-400 hover:underline transition-all duration-200"
        >
          Breakdown
        </button>
      ),
    },
  ], [currency, openDetail]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <PageContainer
      title="Cash Closing"
      subtitle="Denomination-wise cash closing history across locations"
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Cash Closing' }]}
    >
      <div className="animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(217,119,6,0.5)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex items-center gap-3">
            <CalculatorIcon className="h-7 w-7 text-amber-200" />
            <div>
              <h1 className="text-2xl font-bold text-white">Cash Closing History</h1>
              <p className="text-amber-100 mt-1">Review denomination-wise closings, variances, and cashier reconciliation</p>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        {isLoading ? (
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[1, 2, 3].map((i) => <StatCard key={i} title="" value="" loading />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="animate-slide-up"><StatCard title="Total Closings" value={totalItems.toLocaleString('en-IN')} icon={<CalculatorIcon className="h-4 w-4" />} /></div>
            <div className="animate-slide-up" style={{ animationDelay: '50ms' }}><StatCard title="Avg Variance" value={`${avgVariancePct.toFixed(1)}%`} icon={<CurrencyIcon className="h-4 w-4" />} anomaly={avgVariancePct > VARIANCE_THRESHOLD_PCT} /></div>
            <div className="animate-slide-up" style={{ animationDelay: '100ms' }}><StatCard title="With Shortfall" value={shortfallCount.toLocaleString('en-IN')} icon={<AlertIcon className="h-4 w-4" />} anomaly={shortfallCount > 0} /></div>
          </div>
        )}

        {/* Filters */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-48">
              <Select
                options={locationOptions}
                value={locationFilter}
                onChange={(v) => { setLocationFilter(typeof v === 'string' ? v : v[0] ?? ''); setPage(1); }}
                placeholder="All Locations"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">From</span>
              <DatePicker value={startDate ? new Date(startDate + 'T00:00:00') : undefined} onChange={(d: Date) => { setStartDate(toLocalIsoDate(d)); setPage(1); }} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">To</span>
              <DatePicker value={endDate ? new Date(endDate + 'T00:00:00') : undefined} onChange={(d: Date) => { setEndDate(toLocalIsoDate(d)); setPage(1); }} />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-3')}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="text" width="15%" height={16} />
                <Skeleton variant="text" width="12%" height={16} />
                <Skeleton variant="text" width="12%" height={16} />
                <Skeleton variant="text" width="10%" height={16} />
                <Skeleton variant="text" width="10%" height={16} />
                <Skeleton variant="text" width="10%" height={16} />
                <Skeleton variant="text" width="8%" height={16} />
                <Skeleton variant="rect" width={50} height={22} />
              </div>
            ))}
          </div>
        ) : records.length === 0 ? (
          <EmptyState variant="page" icon={<CalculatorEmptyIcon />} title="No cash closings recorded yet" description="Cash closings appear here after cashiers complete their end-of-shift count" />
        ) : (
          <>
            {/* Desktop */}
            <div className={cn(GLASS, 'overflow-hidden hidden sm:block mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Closings ({totalItems.toLocaleString('en-IN')})
                </h3>
              </div>
              <DataTable<CashClosingRecord>
                columns={columns}
                data={records}
              />
            </div>

            {/* Mobile */}
            <div className="block sm:hidden space-y-3 mb-6">
              <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1">
                Closings ({totalItems.toLocaleString('en-IN')})
              </h3>
              {records.map((record) => (
                <ClosingMobileCard key={record.id} record={record} onClick={() => openDetail(record)} />
              ))}
            </div>

            {totalPages > 1 && <div className="mb-6"><Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} pageSize={PAGE_SIZE} onPageChange={setPage} /></div>}
          </>
        )}
      </div>

      {/* Denomination Breakdown Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={detailRecord ? `Cash Closing — ${detailRecord.closed_by_name ?? 'Unknown'}` : 'Cash Closing'}>
        {detailRecord && (
          <div className="p-1">
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 space-y-0.5">
              <p><span className="font-medium text-neutral-700 dark:text-neutral-300">Location:</span> {detailRecord.location_name}</p>
              <p><span className="font-medium text-neutral-700 dark:text-neutral-300">Date:</span> <span className="font-mono tabular-nums">{formatDate(detailRecord.closed_at)}</span></p>
            </div>
            <DenominationBreakdown record={detailRecord} currency={currency} />
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}

export function CashClosingHistoryPage() {
  return (
    <ErrorBoundary>
      <CashClosingHistoryContent />
    </ErrorBoundary>
  );
}
